import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderIcon from '@mui/icons-material/Folder';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { COMPACT_DIALOG_PAPER_SX } from '@/components/shared/dialogStyles';
import {
  EXPORT_FORMATS,
  IMPORT_FORMATS,
  MAX_EXTRACT_RECORDS,
  UPLOAD_SUCCESS_STATUS,
  uploadScheduleFile,
  type ScheduleUploadResult,
  type SchedulingTab,
} from '@/services/scheduling/schedulingService';

const DIALOG_TITLE_SX = {
  p: 0,
  color: 'rgba(0, 0, 0, 0.87)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '20px',
  fontWeight: 500,
  letterSpacing: '.25px',
  lineHeight: '26px',
};

const DIALOG_MESSAGE_SX = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '16px',
  letterSpacing: '.5px',
  lineHeight: '28px',
};

const DIALOG_ACTIONS_SX = { m: 0, mt: 3, p: '0 !important', gap: '12px', justifyContent: 'flex-start' };

const CANCEL_BUTTON_SX = {
  maxHeight: '36px',
  px: '12px',
  color: '#0c479d !important',
  backgroundColor: '#fff !important',
  boxShadow: 'none !important',
  fontWeight: 500,
  letterSpacing: '.0892857143em',
  textTransform: 'none',
};

const PRIMARY_BUTTON_SX = {
  maxHeight: '36px',
  letterSpacing: '0.08em',
  color: 'common.white',
  backgroundColor: '#0c479d !important',
  '&.Mui-disabled': { backgroundColor: '#e0e0e0 !important', color: '#9e9e9e !important' },
};

const toTitleCase = (value: string) => value.replace(/\w\S*/g, (word) => word[0].toUpperCase() + word.slice(1).toLowerCase());

const compactPaper = { paper: { sx: COMPACT_DIALOG_PAPER_SX } };
const importDialogPaper = {
  paper: {
    sx: {
      ...COMPACT_DIALOG_PAPER_SX,
      width: 'min(960px, calc(100vw - 48px))',
      maxWidth: 'min(960px, calc(100vw - 48px))',
    },
  },
};

interface ImportDialogProps {
  open: boolean;
  tab: string;
  source: string;
  // Defaults to the Transportation Scheduler templates for the tab.
  formats?: { format: string; label: string }[];
  title?: string;
  submitLabel?: string;
  onClose: () => void;
  onComplete: (result: ScheduleUploadResult) => void;
}

export const ScheduleImportDialog: React.FC<ImportDialogProps> = ({
  open, tab, source, formats: formatsProp, title, submitLabel = 'IMPORT SCHEDULE', onClose, onComplete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const formats = formatsProp ?? IMPORT_FORMATS.filter((item) => item.tab === tab);
  const [format, setFormat] = useState(formats[0]?.format ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setFormat(formats[0]?.format ?? '');
    setFile(null);
    if (inputRef.current) inputRef.current.value = '';
    // formats is derived from tab; depending on it would reset the form on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const handleImport = async () => {
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      onComplete({ errorMessage: 'The file is not in CSV format', fileName: file.name });
      return;
    }

    setIsImporting(true);
    try {
      onComplete(await uploadScheduleFile(file, format, source));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!isImporting) onClose(); }}
      maxWidth={false}
      slotProps={importDialogPaper}
    >
      <Box sx={{ m: 3 }}>
        <DialogTitle id="qa-title-lbl" sx={DIALOG_TITLE_SX}>{title ?? `Import ${toTitleCase(tab)}`}</DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, mt: 3 }}>
          <Typography id="qa-message-lbl" sx={{ ...DIALOG_MESSAGE_SX, mb: 3 }}>Select the template format</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              select
              fullWidth
              id="qa-templateFormat-sel"
              label="Template Format"
              value={format}
              onChange={(event) => setFormat(event.target.value)}
              disabled={isImporting}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 0' }, minWidth: 0 }}
            >
              {formats.map((item) => (
                <MenuItem key={item.format} value={item.format}>{item.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              id="qa-fileInput-ipt"
              label="Upload File"
              value={file?.name ?? ''}
              onClick={() => inputRef.current?.click()}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <FolderIcon sx={{ cursor: 'pointer' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: { xs: '1 1 100%', sm: '2 1 0' }, minWidth: 0 }}
            />
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button id="qa-cancel-btn" onClick={onClose} disabled={isImporting} sx={CANCEL_BUTTON_SX}>CANCEL</Button>
          <LoadingButton
            id="qa-uploadSchedule-btn"
            variant="contained"
            disableElevation
            onClick={handleImport}
            loading={isImporting}
            loadingText="IMPORTING"
            disabled={!file || !format}
            sx={PRIMARY_BUTTON_SX}
          >
            {submitLabel}
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

interface ExportDialogProps {
  open: boolean;
  tab: SchedulingTab;
  onClose: () => void;
  onDownload: (format: string) => void;
}

export const ScheduleExportDialog: React.FC<ExportDialogProps> = ({ open, tab, onClose, onDownload }) => {
  const formats = EXPORT_FORMATS.filter((item) => item.tab === tab);
  const [format, setFormat] = useState(formats[0]?.format ?? '');

  useEffect(() => {
    if (open) setFormat(EXPORT_FORMATS.find((item) => item.tab === tab)?.format ?? '');
  }, [open, tab]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} slotProps={compactPaper}>
      <Box sx={{ m: 3 }}>
        <DialogTitle id="qa-title-lbl" sx={DIALOG_TITLE_SX}>
          {`Export ${MAX_EXTRACT_RECORDS} entries for ${tab.toLowerCase()} scheduling.`}
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, mt: 3 }}>
          <Typography id="qa-message-lbl" sx={{ ...DIALOG_MESSAGE_SX, mb: 3 }}>
            Exports are limited to {MAX_EXTRACT_RECORDS} entries. Search and Filters will affect which entries are
            exported. Please select the format for this export.
          </Typography>
          <TextField
            select
            fullWidth
            id="qa-templateFormat-sel"
            label="Template Format"
            value={format}
            onChange={(event) => setFormat(event.target.value)}
          >
            {formats.map((item) => (
              <MenuItem key={item.format} value={item.format}>{item.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button id="qa-cancel-btn" onClick={onClose} sx={CANCEL_BUTTON_SX}>CANCEL</Button>
          <Button
            id="qa-downloadSchedule-btn"
            variant="contained"
            disableElevation
            disabled={!format}
            onClick={() => onDownload(format)}
            sx={PRIMARY_BUTTON_SX}
          >
            DOWNLOAD SCHEDULE
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const ERRORS_PAGE_SIZE = 5;

interface UploadResultDialogProps {
  result: ScheduleUploadResult | null;
  operation: string;
  type: string;
  onClose: () => void;
}

export const UploadResultDialog: React.FC<UploadResultDialogProps> = ({ result, operation, type, onClose }) => {
  const [page, setPage] = useState(0);
  const errors = result?.errors ?? [];
  const isSuccess = result?.status === UPLOAD_SUCCESS_STATUS;
  const hasValidationErrors = errors.length > 0 && errors[0].row !== 1;

  useEffect(() => setPage(0), [result]);

  const getMessage = () => {
    if (isSuccess) return `${result?.rowCount ?? 0} ${type} Updated`;
    if (errors.length === 1 && errors[0].row === 1) return errors[0].error;
    if (errors.length > 0) return 'No updates made because some rows failed';
    return result?.errorMessage ? `An error occured: ${result.errorMessage}` : 'An error occured';
  };

  return (
    <Dialog open={!!result} onClose={onClose} maxWidth={false} slotProps={compactPaper}>
      <Box sx={{ m: 3, textAlign: 'center' }}>
        {isSuccess
          ? <CheckCircleIcon sx={{ fontSize: 48, color: '#0c479d' }} />
          : <CancelIcon sx={{ fontSize: 48, color: '#bdbdbd' }} />}
        <Typography id="qa-uploadConfirm-title-lbl" sx={{ ...DIALOG_TITLE_SX, mt: 1 }}>
          {isSuccess ? `${operation} Uploaded Successfully` : `${operation} Upload Failed`}
        </Typography>
        <Typography id="qa-uploadConfirm-message-lbl" sx={{ ...DIALOG_MESSAGE_SX, mt: 1 }}>
          {getMessage()}
        </Typography>

        {hasValidationErrors && (
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell id="qa-uploadConfirm-row-hdr">Row</TableCell>
                  <TableCell id="qa-uploadConfirm-error-hdr">Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.slice(page * ERRORS_PAGE_SIZE, (page + 1) * ERRORS_PAGE_SIZE).map((item, index) => (
                  <TableRow key={`${item.row}-${index}`}>
                    <TableCell>{item.row}</TableCell>
                    <TableCell>{item.error}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={errors.length}
              page={page}
              rowsPerPage={ERRORS_PAGE_SIZE}
              rowsPerPageOptions={[ERRORS_PAGE_SIZE]}
              onPageChange={(_, nextPage) => setPage(nextPage)}
            />
          </Box>
        )}

        <DialogActions sx={{ ...DIALOG_ACTIONS_SX, justifyContent: 'center' }}>
          <Button id="qa-uploadConfirm-okay-btn" variant="contained" disableElevation onClick={onClose} sx={PRIMARY_BUTTON_SX}>
            OKAY
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

interface StatusNoteDialogProps {
  open: boolean;
  orderNumber: string;
  status: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}

export const StatusNoteDialog: React.FC<StatusNoteDialogProps> = ({
  open,
  orderNumber,
  status,
  isSubmitting,
  onCancel,
  onSubmit,
}) => {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  return (
    <Dialog open={open} onClose={() => { if (!isSubmitting) onCancel(); }} maxWidth={false} slotProps={compactPaper}>
      <Box sx={{ m: 3 }}>
        <DialogTitle id="qa-title-lbl" sx={DIALOG_TITLE_SX}>{`Set Order #${orderNumber} to ${status}?`}</DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, mt: 3 }}>
          <Typography id="qa-paragraph-txt" sx={{ ...DIALOG_MESSAGE_SX, mb: 2 }}>
            Setting this shipment to {status} will send the order back to the customer service representative.
            Please add a note to let the representative know why this order needs to be recycled.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={6}
            autoFocus
            id="qa-notes-ipt"
            placeholder="Enter notes for this action"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button id="qa-cancel-btn" onClick={onCancel} disabled={isSubmitting} sx={CANCEL_BUTTON_SX}>CANCEL</Button>
          <LoadingButton
            id="qa-setStatus-btn"
            variant="contained"
            disableElevation
            loading={isSubmitting}
            disabled={!note.trim()}
            onClick={() => onSubmit(note.trim())}
            sx={PRIMARY_BUTTON_SX}
          >
            {`SET TO ${status.toUpperCase()}`}
          </LoadingButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
